import { useTranslation } from 'react-i18next';
import { AccountLink } from '../navigation/account-link.jsx';

const OUTGOING = '\u2192';
const INCOMING = '\u2190';

/**
 * Korean direction particles agree with the preceding noun's final
 * syllable: 으로 after a 받침 (consonant), 로 after a vowel. Non-Hangul
 * names keep the base particle.
 *
 * @param {string} particle — base particle from the locale (e.g. "으로")
 * @param {string} noun — the account name the particle is attached to
 * @returns {string}
 */
export function koreanDirectionParticle(particle, noun) {
	if (particle !== '으로') { return particle; }
	const last = noun.charCodeAt(noun.length - 1);
	const isHangul = last >= 0xAC00 && last <= 0xD7A3;
	if (isHangul && (last - 0xAC00) % 28 === 0) {
		return '로';
	}
	return particle;
}

/**
 * Renders a secondary (sub) line inside a double-entry table cell.
 * Derives the arrow from `amount` (or uses explicit `arrow` override),
 * and auto-adds the direction word (prefix/suffix per locale).
 *
 * @param {Object} props
 * @param {number} [props.amount] — used to derive arrow (neg = →, pos = ←)
 * @param {string} [props.arrow] — explicit arrow, overrides amount-based
 * @param {string} [props.className] — optional class for the <span>
 * @param {import('react').ReactNode} props.children
 * @returns {JSX.Element}
 */
export function SecondaryLine({ amount, arrow: arrowProp, className, children }) {
	const { t } = useTranslation();
	const arrow = arrowProp ?? (amount < 0 ? OUTGOING : INCOMING);
	const isAccountLink = children?.type === AccountLink;
	const isSuffix = t('direction.pos') === 'suffix';

	if (isAccountLink && isSuffix) {
		const name = children?.props?.name || '';
		const baseDir = arrow === OUTGOING ? t('direction.to') : t('direction.from');
		const dir = koreanDirectionParticle(baseDir, name);
		return (
			<div className="secondary-line">
				<span className={className}>{arrow} {children}<span className="secondary-line-dir">{dir}</span></span>
			</div>
		);
	}

	const prefix = isAccountLink ? (arrow === OUTGOING ? t('direction.to') + ' ' : t('direction.from') + ' ') : '';
	return (
		<div className="secondary-line">
			<span className={className}>{arrow} {prefix}{children}</span>
		</div>
	);
}
